import { useEffect, useState } from 'react';
import { Building2, ChevronRight } from 'lucide-react';
import { getCompanyLogoWithAuth } from '../../services/companyFileReader';
import YearSelector from '../YearSelector/YearSelector';
import styles from './ClientPicker.module.css';

/** Logos live in the uploads folder of the selected year, behind auth. */
const ClientLogo = ({ company, year }) => {
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;

    setLogoUrl(null);
    getCompanyLogoWithAuth(company, year)
      .then((url) => {
        if (!url) return;
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        objectUrl = url;
        setLogoUrl(url);
      })
      .catch(() => {
        // Clients without an uploaded logo keep the generic icon.
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [company, year]);

  return (
    <span className={`${styles.icon} ${logoUrl ? styles.iconLogo : ''}`}>
      {logoUrl
        ? <img src={logoUrl} alt="" className={styles.logo} />
        : <Building2 size={25} aria-hidden="true" />}
    </span>
  );
};

const ClientPicker = ({
  clients = [],
  records = [],
  selectedYear,
  availableYears = [],
  onYearChange,
  onSelect,
}) => (
  <main className={styles.page}>
    <header className={styles.hero}>
      <div className={styles.heroContent}>
        <div>
          <p className={styles.eyebrow}>Accesso Supervisor</p>
          <p style={{fontSize: '32px'}}>Seleziona un cliente</p>
          <p className={styles.subtitle}>
            Scegli l'anno di analisi e apri la dashboard del cliente.
          </p>
        </div>
        <YearSelector
          years={availableYears}
          value={selectedYear}
          onChange={onYearChange}
          tone="dark"
          label="Anno di analisi"
        />
      </div>
    </header>

    <section className={styles.content} aria-live="polite">
      <div className={styles.sectionHeader}>
        <div>
          <p className={styles.sectionEyebrow}>Clienti disponibili</p>
          <h2>Dati {selectedYear || '—'}</h2>
        </div>
        <span className={styles.count}>{clients.length} clienti</span>
      </div>

      <div className={styles.grid}>
        {clients.map((client) => {
          const cities = Object.keys(client.cities || {});
          const record = records.find(
            (item) => Number(item.anno) === Number(selectedYear)
              && String(item.azienda).toUpperCase() === String(client.company).toUpperCase()
          );
          const score = record?.indice_sintetico ?? record?.['indice sintetico'];
          const isAvailable = Boolean(record);

          return (
            <button
              type="button"
              className={styles.card}
              key={client.clientId || client.company}
              onClick={() => onSelect(client.company)}
              disabled={!isAvailable}
              aria-label={
                isAvailable
                  ? `Apri ${client.company}, anno ${selectedYear}`
                  : `${client.company}, dati non disponibili per ${selectedYear}`
              }
            >
              <ClientLogo company={client.company} year={selectedYear} />
              <span className={styles.details}>
                <strong>{client.company}</strong>
                <small>{cities.length ? cities.join(' · ') : 'Dashboard cliente'}</small>
                {!isAvailable && <em>Non disponibile nel {selectedYear}</em>}
              </span>
              {typeof score === 'number' && (
                <span className={styles.score}>
                  <b>{score.toFixed(1)}</b>
                  <small>/10</small>
                </span>
              )}
              <ChevronRight size={20} />
            </button>
          );
        })}
      </div>
    </section>
  </main>
);

export default ClientPicker;
